import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppService } from '../core/services/app.service';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { NgClass } from '@angular/common';
import { ThemeCustomizerComponent } from './theme-customizer';
import { HeaderComponent } from './header';
import { SidebarComponent } from './sidebar';
import { FooterComponent } from './footer';
import { PermissionService } from '../core/services/api/permission.service';
import { InactivityService } from '../core/services/inactivity.service';
import { SidebarNavService } from '../core/services/sidebar-nav.service';
import { setPermissions } from '../store/actions/permission.actions';
import { InactivityWarningModalComponent, LoaderComponent } from '@sukna/shared';
import { filter, Subscription, take } from 'rxjs';

@Component({
    selector: 'app-root',
    templateUrl: './app-layout.html',
    imports: [NgClass, RouterModule, ThemeCustomizerComponent, HeaderComponent, FooterComponent, SidebarComponent, InactivityWarningModalComponent, LoaderComponent],
})
export class AppLayout implements OnInit, OnDestroy {
    private inactivityService = inject(InactivityService);
    private sidebarNavService = inject(SidebarNavService);
    private navigationSubscription?: Subscription;
    private previousUrl = '';
    private wasMobile = false;
    private resizeHandler?: () => void;

    store: any;
    showTopButton = false;
    showWarningModal = false;
    countdown = 60;

    constructor(
        public storeData: Store<any>,
        private service: AppService,
        private router: Router,
        private _permissionService: PermissionService
    ) {
        this.initStore();
        this.inactivityService.init(); 
        this._permissionService.getPermissionByUser().subscribe((response) => {
          const data = Array.isArray(response)
            ? response
            : Array.isArray((response as { data?: unknown })?.data)
              ? (response as { data: Array<{ code: string }> }).data
              : [];
          this.storeData.dispatch(setPermissions({ data }));
        });
    }
    headerClass = '';
    ngOnInit() {
        this.previousUrl = this.router.url;
        this.syncSidebarForRoute(this.previousUrl, '');
        this.navigationSubscription = this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event) => {
                const nextUrl = event.urlAfterRedirects;
                this.syncSidebarForRoute(nextUrl, this.previousUrl);
                this.previousUrl = nextUrl;
            });

        this.initAnimation();
        this.toggleLoader();

        // Collapse sidebar on initial mobile load and whenever window crosses into mobile
        this.wasMobile = window.innerWidth < 1024;
        if (this.wasMobile) {
            this.storeData.dispatch({ type: 'setSidebar', payload: false });
        }
        this.resizeHandler = () => {
            const isMobile = window.innerWidth < 1024;
            if (isMobile && !this.wasMobile) {
                this.storeData.dispatch({ type: 'setSidebar', payload: false });
            }
            this.wasMobile = isMobile;
        };
        window.addEventListener('resize', this.resizeHandler);

        window.addEventListener('scroll', () => {
            if (document.body.scrollTop > 50 || document.documentElement.scrollTop > 50) {
                this.showTopButton = true;
            } else {
                this.showTopButton = false;
            }
        });
        this.inactivityService.showWarning$.subscribe(show => {
            this.showWarningModal = show;
        });

        this.inactivityService.countdown$.subscribe(countdown => {
            this.countdown = countdown;
        });
    }

    ngOnDestroy() {
        this.navigationSubscription?.unsubscribe();
        window.removeEventListener('scroll', () => {});
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
        }
        this.inactivityService.destroy();
    }

    private syncSidebarForRoute(nextUrl: string, previousUrl: string): void {
        this.storeData
            .select((d) => d.index.sidebar as boolean)
            .pipe(take(1))
            .subscribe((currentCollapsed) => {
                const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;

                // Same `sidebar` flag means "open" on mobile but "collapsed" on desktop — never auto-open on mobile.
                if (isMobile) {
                    if (currentCollapsed) {
                        this.storeData.dispatch({ type: 'setSidebar', payload: false });
                    }
                    return;
                }

                const nextCollapsed = this.sidebarNavService.resolveSidebarForNavigation(
                    previousUrl,
                    nextUrl,
                    currentCollapsed
                );
                if (nextCollapsed !== undefined && nextCollapsed !== currentCollapsed) {
                    this.storeData.dispatch({ type: 'setSidebar', payload: nextCollapsed });
                }
            });
    }

    initAnimation() {
        this.service.changeAnimation();
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.service.changeAnimation();
            }
        });

        const ele: any = document.querySelector('.animation');
        ele.addEventListener('animationend', () => {
            this.service.changeAnimation('remove');
        });
    }

    toggleLoader() {
        this.storeData.dispatch({ type: 'toggleMainLoader', payload: true });
        setTimeout(() => {
            this.storeData.dispatch({ type: 'toggleMainLoader', payload: false });
        }, 500);
    }

    async initStore() {
        this.storeData
            .select((d) => d.index)
            .subscribe((d) => {
                this.store = d;
            });
    }

    goToTop() {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
    }

    onResumeSession() {
        this.inactivityService.resumeSession();
    }
}
